import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, FormHelperText, Stack } from '@mui/material'
import { getCodesDropdown } from 'api/accessCode'
import { getOrganizations } from 'api/organization'
import { createTeamInAdmin, createTeamInOrg } from 'api/teams'
import { getUsersByAccessCode } from 'api/users'
import { AxiosResponse } from 'axios'
import FormAutocomplete from 'components/form-elements/FormAutocomplete'
import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import FormSelect, { Option } from 'components/form-elements/FormSelect'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import { FIELD_IS_REQUIRED } from 'const'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { EOp, PERSON_CENTERED_TEAM_HINT, TeamType, TeamTypeOptions, UserTeamMember } from 'types'
import zod, { TypeOf } from 'zod'

interface TeamAddFormProps {
  isOrg?: boolean
  onClose: () => void
  onRefetch: () => void
}

const formSchema = zod
  .object({
    // Admin: selected via Organization dropdown. Org: auto-set from user context.
    // FormAutocomplete may pass string (during typing) or null (when cleared)
    organizationId: zod
      .union([zod.number(), zod.string(), zod.null()])
      .optional()
      .transform((val): number | undefined =>
        typeof val === 'number' && val > 0 ? val : undefined,
      ),
    // FormAutocomplete may pass string (during typing) or null (when cleared)
    accessCodeId: zod
      .union([zod.number(), zod.string(), zod.null()])
      .superRefine((val, ctx) => {
        // Don't show error while user is typing (string value)
        if (typeof val === 'string' && val.length > 0) return
        if (typeof val !== 'number' || val < 1) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, message: FIELD_IS_REQUIRED })
        }
      })
      .transform((val): number => (typeof val === 'number' ? val : 0)),
    name: zod.string().min(1, FIELD_IS_REQUIRED).max(50, 'Please enter a maximum of 50 characters'),
    // Starts empty, so the union carries '' until the user picks a type.
    type: zod.union([zod.nativeEnum(TeamType), zod.literal('')]).superRefine((val, ctx) => {
      if (!val) {
        ctx.addIssue({ code: zod.ZodIssueCode.custom, message: FIELD_IS_REQUIRED })
      }
    }),
    customTypeDescription: zod
      .string()
      .max(255, 'Please enter a maximum of 255 characters')
      .optional(),
    members: zod
      .array(zod.object({ label: zod.string(), value: zod.number() }))
      .min(1, FIELD_IS_REQUIRED),
  })
  // Mirrors the API rule: a Customized Team must describe its type.
  .superRefine((data, ctx) => {
    if (data.type === TeamType.CustomizedTeam && !data.customTypeDescription?.trim()) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        message: FIELD_IS_REQUIRED,
        path: ['customTypeDescription'],
      })
    }
  })

type FormType = TypeOf<typeof formSchema>

export function TeamAddForm({ isOrg = false, onClose, onRefetch }: TeamAddFormProps) {
  const { user } = useAuthContext()

  const methods = useForm<FormType>({
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: {
      organizationId: isOrg ? user?.organizationId || 0 : 0,
      accessCodeId: 0,
      name: '',
      type: '',
      customTypeDescription: '',
      members: [],
    },
  })

  const selectedType = useWatch({ control: methods.control, name: 'type' })

  // Watch organizationId - can be number (selected), string (typing), or null (cleared)
  const selectedOrganizationId = useWatch({
    control: methods.control,
    name: 'organizationId',
  }) as number | string | null

  // Raw value from FormAutocomplete - can be number (selected), string (typing), or null (cleared)
  const selectedAccessCodeId = useWatch({
    control: methods.control,
    name: 'accessCodeId',
  }) as number | string | null

  // Numeric organizationId for filtering (FormAutocomplete may pass string/null)
  const activeOrganizationId = useMemo((): number | null => {
    if (typeof selectedOrganizationId === 'number' && selectedOrganizationId > 0)
      return selectedOrganizationId
    return null
  }, [selectedOrganizationId])

  // Load organizations for admin portal Organization dropdown
  const { data: organizationsData, isLoading: isLoadingOrgs } = useQuery(
    'organizations',
    () => getOrganizations(),
    { enabled: !isOrg },
  )

  const organizationOptions: Option[] = useMemo(() => {
    if (!organizationsData) return []
    return organizationsData.data.rows.map((org) => ({
      label: org.name,
      value: org.id,
    }))
  }, [organizationsData])

  // Load access codes for State/Unit dropdown, filtered by organization
  const { data: codesData, isLoading: isLoadingCodes } = useQuery(
    ['codesDropdown', activeOrganizationId],
    () =>
      getCodesDropdown({
        onlyValid: false,
        onlyWithMembers: true,
        filter:
          !isOrg && activeOrganizationId
            ? {
                organizationId: { type: 'number', op: EOp.equals, value: activeOrganizationId },
              }
            : undefined,
      }),
    {
      enabled: isOrg || !!activeOrganizationId,
    },
  )

  const codeOptions: Option[] = useMemo(() => {
    if (!codesData) return []
    return codesData.data.map((item) => ({
      value: item.id,
      label: [item.specificState, item.specificUnitName, item.code].filter(Boolean).join('/'),
    }))
  }, [codesData])

  // Reset accessCodeId and members when organization changes (admin portal)
  useEffect(() => {
    if (!isOrg) {
      methods.setValue('accessCodeId', 0)
      methods.setValue('members', [])
    }
  }, [selectedOrganizationId, isOrg, methods])

  // Load members based on selected access code
  const { data: membersData, isLoading: isLoadingMembers } = useQuery<
    AxiosResponse<UserTeamMember[]>,
    unknown,
    UserTeamMember[]
  >(
    ['members', selectedAccessCodeId],
    () =>
      getUsersByAccessCode({
        filter: {
          accessCodeId: {
            type: 'number',
            op: EOp.equals,
            value: selectedAccessCodeId as number,
          },
        },
      }),
    {
      enabled: typeof selectedAccessCodeId === 'number' && selectedAccessCodeId > 0,
      select: (data) => data.data,
    },
  )

  // Member options
  const memberOptions: Option[] = useMemo(() => {
    if (!membersData) return []

    return membersData.map((member) => ({
      label: `${member.firstname} ${member.lastname}`,
      value: member.id,
    }))
  }, [membersData])

  // Reset members when access code changes
  useEffect(() => {
    methods.setValue('members', [])
  }, [selectedAccessCodeId, methods])

  const createMutation = useMutation(isOrg ? createTeamInOrg : createTeamInAdmin)

  const onSubmitHandler = async (data: FormType) => {
    try {
      await createMutation.mutateAsync({
        organizationId: data.organizationId || null,
        accessCodeId: data.accessCodeId,
        name: data.name,
        // Validation above guarantees a type is selected by this point.
        type: data.type as TeamType,
        customTypeDescription:
          data.type === TeamType.CustomizedTeam ? data.customTypeDescription?.trim() : undefined,
        memberIds: data.members.map((member) => member.value),
      })

      NotificationSys.showSuccess('Team successfully created')
      methods.reset()
      onRefetch()
      onClose()
    } catch (error) {
      console.error('Error creating team:', error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmitHandler)}
        width="100%"
        noValidate
      >
        <Stack spacing={2}>
          {!isOrg && (
            <FormAutocomplete
              label="Organization"
              name="organizationId"
              options={organizationOptions}
              isLoading={isLoadingOrgs}
            />
          )}

          <FormAutocomplete
            label="State/Unit"
            name="accessCodeId"
            options={codeOptions}
            isLoading={isLoadingCodes}
            disabled={
              !isOrg && (typeof selectedOrganizationId !== 'number' || selectedOrganizationId <= 0)
            }
          />

          <FormInput label="Name" name="name" fullWidth />

          <Stack spacing={0.5}>
            <FormSelect name="type" label="Type" fullWidth options={TeamTypeOptions} />

            {selectedType === TeamType.PersonCenteredTeam && (
              <FormHelperText>{PERSON_CENTERED_TEAM_HINT}</FormHelperText>
            )}
          </Stack>

          {selectedType === TeamType.CustomizedTeam && (
            <FormInput label="Team Type Description" name="customTypeDescription" fullWidth />
          )}

          <FormMultipleSelect
            label="Select Members"
            name="members"
            options={memberOptions}
            defaultValue={[]}
            isLoading={isLoadingMembers}
            disabled={!selectedAccessCodeId}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>

            <LoadingButton loading={createMutation.isLoading} variant="contained" type="submit">
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}
